/** @format */

import { Brand, Paper, UpsertPaperInput } from "@question-bank/shared";
import { getApiBaseUrl } from "./questions";
import { authFetch } from "./session";

const JSON_HEADERS: HeadersInit = {
	"Content-Type": "application/json",
};

/**
 * List all papers for a brand, optionally filtered by subject.
 */
export const listPapers = async (brand: Brand, subject?: string): Promise<Paper[]> => {
	const baseUrl = getApiBaseUrl(brand);
	const params = new URLSearchParams();
	if (subject) {
		params.set("subject", subject);
	}
	const query = params.toString();
	const url = `${baseUrl}/papers${query ? `?${query}` : ""}`;

	const response = await fetch(url, { method: "GET", headers: JSON_HEADERS });
	if (!response.ok) {
		throw new Error(`Failed to list papers: ${response.statusText}`);
	}
	const body = (await response.json()) as { data: Paper[] };
	return body.data;
};

/**
 * Get a single stored paper by id. Returns `null` when no record exists yet.
 */
export const getPaper = async (brand: Brand, paperId: string): Promise<Paper | null> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/papers/${encodeURIComponent(paperId)}`;

	const response = await fetch(url, { method: "GET", headers: JSON_HEADERS });
	if (response.status === 404) {
		return null;
	}
	if (!response.ok) {
		throw new Error(`Failed to get paper: ${response.statusText}`);
	}
	const body = (await response.json()) as { data: Paper };
	return body.data;
};

/**
 * Create or replace a paper record (requires auth).
 */
export const upsertPaper = async (brand: Brand, paperId: string, paper: UpsertPaperInput): Promise<Paper> => {
	const baseUrl = getApiBaseUrl(brand);
	const url = `${baseUrl}/papers/${encodeURIComponent(paperId)}`;

	const response = await authFetch(url, {
		method: "PUT",
		headers: JSON_HEADERS,
		body: JSON.stringify(paper),
	});
	if (!response.ok) {
		const body = (await response.json().catch(() => ({}))) as { error?: { message?: string } };
		throw new Error(body.error?.message ?? `Failed to save paper: ${response.statusText}`);
	}
	const result = (await response.json()) as { data: Paper };
	return result.data;
};

/**
 * Upload a PDF via the presigned-URL flow and return the stored asset key.
 */
export const uploadPdf = async (brand: Brand, file: File): Promise<string> => {
	const baseUrl = getApiBaseUrl(brand);

	const presignResponse = await authFetch(`${baseUrl}/assets`, {
		method: "POST",
		headers: JSON_HEADERS,
		body: JSON.stringify({ filename: file.name, contentType: "application/pdf" }),
	});
	if (!presignResponse.ok) {
		throw new Error("Unable to get upload URL");
	}
	const { data } = (await presignResponse.json()) as { data: { uploadUrl: string; assetKey: string } };

	const uploadResponse = await fetch(data.uploadUrl, {
		method: "PUT",
		headers: { "Content-Type": "application/pdf" },
		body: file,
	});
	if (!uploadResponse.ok) {
		throw new Error("Failed to upload PDF");
	}

	return data.assetKey;
};
